import {
  lookupNearbyPlaces,
  type PlaceLookupAddressComponent,
  type PlaceLookupPlace,
} from "./placeLookup";

export type PlaceResolverResult = {
  landmarkName: string | null;
  roadName: string | null;
  areaName: string | null;
};

type PlaceContext = {
  roadName?: string | null;
  areaName?: string | null;
  cityName?: string | null;
  regionName?: string | null;
  countryName?: string | null;
};

/* ================================
   HELPERS
================================ */

function cleanText(value?: unknown): string | null {
  if (typeof value !== "string") return null;

  const cleaned = value
    .replace(/\s+/g, " ")
    .replace(/[.,\s]+$/g, "")
    .trim();

  return cleaned.length > 0 ? cleaned : null;
}

function normalizeForCompare(value?: unknown): string {
  return (
    cleanText(value)
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/gi, " ")
      .trim() ?? ""
  );
}

function sameText(a?: unknown, b?: unknown): boolean {
  const left = normalizeForCompare(a);
  const right = normalizeForCompare(b);

  return !!left && !!right && left === right;
}

function pickFirstText(...values: (unknown | null | undefined)[]): string | null {
  for (const value of values) {
    const cleaned = cleanText(value);
    if (cleaned) return cleaned;
  }
  return null;
}

function looksLikePlusCode(value?: unknown): boolean {
  const text = cleanText(value)?.toUpperCase();
  if (!text) return false;

  return /^[A-Z0-9]{2,8}\+[A-Z0-9]{2,8}$/.test(text);
}

function componentHasType(
  component: PlaceLookupAddressComponent,
  type: string
): boolean {
  return Array.isArray(component.types) && component.types.includes(type);
}

function getComponentText(
  component?: PlaceLookupAddressComponent | null
): string | null {
  if (!component) return null;

  return pickFirstText(
    component.longText,
    component.long_name,
    component.shortText,
    component.short_name
  );
}

function getFirstComponentTextByTypes(
  addressComponents: PlaceLookupAddressComponent[],
  types: string[]
): string | null {
  for (const component of addressComponents) {
    if (types.some((type) => componentHasType(component, type))) {
      const text = getComponentText(component);
      if (text) return text;
    }
  }

  return null;
}

function looksLikeFullAddress(name?: unknown): boolean {
  const text = cleanText(name);
  if (!text) return false;

  const lower = text.toLowerCase();

  return (
    lower.includes(",") ||
    /\broad\b/.test(lower) ||
    /\bstreet\b/.test(lower) ||
    /\bavenue\b/.test(lower) ||
    /\bhighway\b/.test(lower) ||
    /\bkenya\b/.test(lower)
  );
}

function isWeakGenericText(name?: unknown): boolean {
  const text = cleanText(name);
  if (!text) return true;

  const lower = text.toLowerCase();

  return (
    lower === "road" ||
    lower === "street" ||
    lower === "route" ||
    lower === "avenue" ||
    lower === "kenya" ||
    lower === "nairobi" ||
    lower === "unnamed road" ||
    lower.includes("unnamed")
  );
}

function sanitizeRoadCandidate(
  value: unknown,
  context: PlaceContext
): string | null {
  const text = cleanText(value);
  if (!text) return null;

  if (looksLikePlusCode(text)) return null;
  if (isWeakGenericText(text)) return null;

  if (
    sameText(text, context.areaName) ||
    sameText(text, context.cityName) ||
    sameText(text, context.regionName) ||
    sameText(text, context.countryName)
  ) {
    return null;
  }

  return text;
}

function sanitizeAreaCandidate(
  value: unknown,
  context: PlaceContext
): string | null {
  const text = cleanText(value);
  if (!text) return null;

  if (looksLikePlusCode(text)) return null;
  if (isWeakGenericText(text)) return null;

  if (
    sameText(text, context.roadName) ||
    sameText(text, context.countryName)
  ) {
    return null;
  }

  return text;
}

function sanitizeLandmarkCandidate(
  value: unknown,
  context: PlaceContext
): string | null {
  const text = cleanText(value);
  if (!text) return null;

  if (looksLikePlusCode(text)) return null;
  if (isWeakGenericText(text)) return null;
  if (looksLikeFullAddress(text)) return null;

  if (
    sameText(text, context.roadName) ||
    sameText(text, context.areaName) ||
    sameText(text, context.cityName) ||
    sameText(text, context.regionName) ||
    sameText(text, context.countryName)
  ) {
    return null;
  }

  return text;
}

function getPlacePrimaryName(place: PlaceLookupPlace): string | null {
  return pickFirstText(place.displayName, place.name);
}

function scoreLandmarkTypes(types: string[] = []): number {
  let score = 0;

  for (const type of types) {
    if (
      [
        "shopping_mall",
        "supermarket",
        "market",
        "hospital",
        "school",
        "bank",
        "pharmacy",
        "gas_station",
      ].includes(type)
    ) {
      score += 60;
      continue;
    }

    if (
      [
        "store",
        "restaurant",
        "premise",
        "subpremise",
      ].includes(type)
    ) {
      score += 40;
      continue;
    }

    if (
      [
        "point_of_interest",
        "establishment",
      ].includes(type)
    ) {
      score += 20;
    }
  }

  return score;
}

function scoreLandmarkKeywords(name: string): number {
  const lower = name.toLowerCase();
  let score = 0;

  if (
    /\b(market|mall|plaza|centre|center|school|hospital|clinic|pharmacy|bank|church|mosque|stage|petrol|station|supermarket|shop)\b/i.test(
      lower
    )
  ) {
    score += 35;
  }

  if (name.length >= 4) score += 5;
  if (name.length >= 8) score += 5;

  return score;
}

function extractAddressContext(
  addressComponents: PlaceLookupAddressComponent[] = []
): PlaceContext {
  const countryName = getFirstComponentTextByTypes(addressComponents, ["country"]);

  const cityName = getFirstComponentTextByTypes(addressComponents, [
    "locality",
    "postal_town",
    "administrative_area_level_3",
  ]);

  const regionName = getFirstComponentTextByTypes(addressComponents, [
    "administrative_area_level_2",
    "administrative_area_level_1",
  ]);

  return {
    cityName,
    regionName,
    countryName,
  };
}

function chooseBestLandmark(
  places: PlaceLookupPlace[],
  baseContext: PlaceContext
): string | null {
  let bestName: string | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;

  for (const place of places) {
    const placeContext = {
      ...baseContext,
      ...extractAddressContext(place.addressComponents ?? []),
    };

    const candidate = sanitizeLandmarkCandidate(
      getPlacePrimaryName(place),
      placeContext
    );

    if (!candidate) continue;

    let score = 0;
    score += scoreLandmarkTypes(place.types ?? []);
    score += scoreLandmarkKeywords(candidate);

    if (place.displayName && !looksLikeFullAddress(place.displayName)) {
      score += 5;
    }

    if (score > bestScore) {
      bestScore = score;
      bestName = candidate;
    }
  }

  return bestScore > 0 ? bestName : null;
}

function extractRoadAndArea(
  addressComponents: PlaceLookupAddressComponent[] = []
): {
  roadName: string | null;
  areaName: string | null;
  cityName: string | null;
  regionName: string | null;
  countryName: string | null;
} {
  const baseContext = extractAddressContext(addressComponents);

  const areaName = (() => {
    const candidates = [
      getFirstComponentTextByTypes(addressComponents, [
        "sublocality_level_1",
        "sublocality",
      ]),
      getFirstComponentTextByTypes(addressComponents, ["neighborhood"]),
      getFirstComponentTextByTypes(addressComponents, [
        "administrative_area_level_4",
      ]),
      getFirstComponentTextByTypes(addressComponents, [
        "administrative_area_level_3",
      ]),
      getFirstComponentTextByTypes(addressComponents, ["locality"]),
    ];

    for (const candidate of candidates) {
      const cleaned = sanitizeAreaCandidate(candidate, baseContext);
      if (cleaned) return cleaned;
    }

    return null;
  })();

  const roadName = (() => {
    const candidates = [
      getFirstComponentTextByTypes(addressComponents, ["route"]),
      getFirstComponentTextByTypes(addressComponents, ["street_address"]),
    ];

    for (const candidate of candidates) {
      const cleaned = sanitizeRoadCandidate(candidate, {
        ...baseContext,
        areaName,
      });
      if (cleaned) return cleaned;
    }

    return null;
  })();

  return {
    roadName,
    areaName,
    cityName: baseContext.cityName ?? null,
    regionName: baseContext.regionName ?? null,
    countryName: baseContext.countryName ?? null,
  };
}

/* ================================
   MAIN RESOLVER
================================ */

export async function resolveNearbyPlace(
  lat: number,
  lng: number
): Promise<PlaceResolverResult> {
  try {
    const places = await lookupNearbyPlaces(lat, lng);

    if (!places.length) {
      return {
        landmarkName: null,
        roadName: null,
        areaName: null,
      };
    }

    let globalCityName: string | null = null;
    let globalRegionName: string | null = null;
    let globalCountryName: string | null = null;

    for (const place of places) {
      const context = extractAddressContext(place.addressComponents ?? []);

      if (!globalCityName && context.cityName) {
        globalCityName = context.cityName;
      }

      if (!globalRegionName && context.regionName) {
        globalRegionName = context.regionName;
      }

      if (!globalCountryName && context.countryName) {
        globalCountryName = context.countryName;
      }

      if (globalCityName && globalRegionName && globalCountryName) {
        break;
      }
    }

    let roadName: string | null = null;
    let areaName: string | null = null;

    for (const place of places) {
      const extracted = extractRoadAndArea(place.addressComponents ?? []);

      if (!areaName && extracted.areaName) {
        areaName = extracted.areaName;
      }

      if (!roadName) {
        const validRoad = sanitizeRoadCandidate(extracted.roadName, {
          areaName: areaName ?? extracted.areaName,
          cityName: extracted.cityName ?? globalCityName,
          regionName: extracted.regionName ?? globalRegionName,
          countryName: extracted.countryName ?? globalCountryName,
        });

        if (validRoad) {
          roadName = validRoad;
        }
      }

      if (roadName && areaName) break;
    }

    const landmarkName = chooseBestLandmark(places, {
      roadName,
      areaName,
      cityName: globalCityName,
      regionName: globalRegionName,
      countryName: globalCountryName,
    });

    return {
      landmarkName,
      roadName,
      areaName,
    };
  } catch {
    console.warn("Nearby place resolution failed.");

    return {
      landmarkName: null,
      roadName: null,
      areaName: null,
    };
  }
}
